import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, MessageCircle, Trash2, Pin, PinOff, StickyNote, CalendarClock,
  Edit2, Settings, Calendar, Phone, Clock, ChevronRight, X,
} from "lucide-react";

const onlyDigits = (s) => (s || "").replace(/\D/g, "");
const waUrl = (phone, msg) => {
  const d = onlyDigits(phone);
  if (!d) return null;
  const full = d.startsWith("55") ? d : "55" + d;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg || "")}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};
const isOverdue = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getTime() < Date.now() - 3600 * 1000; } catch { return false; }
};
const isFuture = (iso) => {
  if (!iso) return false;
  try { return new Date(iso).getTime() > Date.now(); } catch { return false; }
};
// converte ISO UTC para o formato esperado pelo input datetime-local
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const localInputToIso = (s) => {
  if (!s) return null;
  try { return new Date(s).toISOString(); } catch { return null; }
};

export default function MasterCRM() {
  const nav = useNavigate();
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [detailLead, setDetailLead] = useState(null);
  const [quickNoteLead, setQuickNoteLead] = useState(null);
  const [showAgenda, setShowAgenda] = useState(false);
  const [showManageStages, setShowManageStages] = useState(false);
  const [dragLead, setDragLead] = useState(null);

  const load = async () => {
    const [s, l] = await Promise.all([
      api.get("/master/crm/stages", { _cache: false }),
      api.get("/master/crm/leads", { _cache: false }),
    ]);
    setStages(s.data || []);
    setLeads(l.data || []);
  };
  useEffect(() => { load(); }, []);

  const filteredLeads = useMemo(() => {
    if (!search) return leads;
    const q = search.toLowerCase();
    return leads.filter(l => `${l.name || ""} ${l.email || ""} ${l.phone || ""} ${l.company || ""}`.toLowerCase().includes(q));
  }, [leads, search]);

  const byStage = useMemo(() => {
    const map = {};
    stages.forEach(s => { map[s.id] = []; });
    filteredLeads.forEach(l => {
      if (map[l.stage_id]) map[l.stage_id].push(l);
      else if (stages[0]) map[stages[0].id].push(l);
    });
    return map;
  }, [stages, filteredLeads]);

  const agendaCount = useMemo(
    () => leads.filter(l => l.next_contact_at).length,
    [leads]
  );

  const moveLead = async (lead, newStageId) => {
    if (!lead || lead.stage_id === newStageId) return;
    setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, stage_id: newStageId } : l));
    try {
      const r = await api.put(`/master/crm/leads/${lead.id}`, { stage_id: newStageId });
      // atualiza com events vindos do backend
      setLeads(prev => prev.map(l => l.id === lead.id ? r.data : l));
    } catch (e) {
      toast.error("Erro ao mover lead");
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="master-crm-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => nav("/master")} className="text-xs text-neutral-500 hover:text-black mb-2 inline-flex items-center gap-1">
            <ArrowLeft size={12}/> Voltar
          </button>
          <p className="label-tiny mb-2">Master</p>
          <h1 className="font-display text-4xl tracking-tight">CRM</h1>
          <p className="text-sm text-neutral-500 mt-1">Acompanhe leads, lojistas em teste e clientes pagantes em um único pipeline.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar lead…" className="w-[220px]" data-testid="crm-search-input"/>
          <Button variant="outline" onClick={() => setShowAgenda(true)} data-testid="agenda-button">
            <CalendarClock size={14} className="mr-1.5"/> Agenda {agendaCount > 0 && <span className="ml-1 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">{agendaCount}</span>}
          </Button>
          <Button variant="outline" onClick={() => setShowManageStages(true)} data-testid="manage-stages-button">
            <Settings size={14} className="mr-1.5"/> Etapas
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-lead-button">
            <Plus size={16} className="mr-1.5"/> Novo lead
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto pb-3">
        <div className="flex gap-3 min-w-max">
          {stages.map(s => (
            <div
              key={s.id}
              className="w-72 flex-shrink-0 rounded-lg border border-[#E5E5E5] bg-neutral-50 dark:bg-white/5 p-3"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragLead) moveLead(dragLead, s.id); setDragLead(null); }}
              data-testid={`stage-${s.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ background: s.color }}/>
                  <h3 className="text-sm font-medium">{s.name}</h3>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-white border border-[#E5E5E5]">{(byStage[s.id] || []).length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {(byStage[s.id] || []).map(l => (
                  <LeadCard key={l.id} lead={l}
                    onOpen={() => setDetailLead(l)}
                    onQuickNote={() => setQuickNoteLead(l)}
                    onDragStart={() => setDragLead(l)}
                    onDragEnd={() => setDragLead(null)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <NewLeadDialog open={showCreate} stages={stages} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }}/>
      <LeadDetailDialog lead={detailLead} stages={stages} onClose={() => setDetailLead(null)} onChanged={() => load()} onDeleted={() => { setDetailLead(null); load(); }}/>
      <QuickNoteDialog lead={quickNoteLead} onClose={() => setQuickNoteLead(null)} onSaved={() => { setQuickNoteLead(null); load(); }}/>
      <AgendaDialog open={showAgenda} onClose={() => setShowAgenda(false)} onOpenLead={(l) => { setShowAgenda(false); setDetailLead(l); }} onChanged={load}/>
      <ManageStagesDialog open={showManageStages} onClose={() => setShowManageStages(false)} stages={stages} onChanged={load}/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Lead Card
// ─────────────────────────────────────────────────────────────────────────────
function LeadCard({ lead, onOpen, onQuickNote, onDragStart, onDragEnd }) {
  const wa = waUrl(lead.phone, `Olá ${lead.name || ""}, tudo bem?`);
  const notes = lead.notes_list || [];
  const pinned = notes.find(n => n.pinned);
  const last = !pinned && notes.length > 0 ? notes[notes.length - 1] : null;
  const noteToShow = pinned || last;
  const nc = lead.next_contact_at;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      className="bg-white border border-[#E5E5E5] rounded-md p-3 cursor-pointer hover:shadow-sm transition group"
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium truncate flex-1">{lead.name || "(sem nome)"}</p>
        {lead.source === "tenant" && (
          <span className="text-[9px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 flex-shrink-0">Loja</span>
        )}
      </div>
      {lead.company && <p className="text-[11px] text-neutral-500 truncate mt-0.5">{lead.company}</p>}
      <div className="text-[11px] text-neutral-500 mt-1 space-y-0.5">
        {lead.email && <div className="truncate">{lead.email}</div>}
        {lead.phone && <div className="truncate">{lead.phone}</div>}
        {lead.plan && <div className="truncate">Plano: <span className="font-medium">{lead.plan}</span></div>}
      </div>

      {nc && (
        <div className={`mt-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${isOverdue(nc) ? "bg-red-100 text-red-700" : isFuture(nc) ? "bg-blue-100 text-blue-700" : "bg-neutral-100 text-neutral-700"}`}>
          <CalendarClock size={10}/> {fmtDateTime(nc)}
        </div>
      )}

      {noteToShow && (
        <div className={`mt-2 text-[11px] px-2 py-1.5 rounded border flex items-start gap-1 ${pinned ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-neutral-50 border-neutral-200 text-neutral-700"}`}>
          {pinned ? <Pin size={10} className="mt-0.5 flex-shrink-0"/> : <StickyNote size={10} className="mt-0.5 flex-shrink-0"/>}
          <span className="line-clamp-2" title={noteToShow.text}>{noteToShow.text}</span>
        </div>
      )}

      <div className="mt-2 flex items-center gap-2 opacity-60 group-hover:opacity-100 transition">
        {wa && (
          <button onClick={(e)=>{ e.stopPropagation(); window.open(wa, "_blank", "noopener,noreferrer"); }}
            className="text-[11px] inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800"
            data-testid={`lead-wa-${lead.id}`}>
            <MessageCircle size={11}/> WhatsApp
          </button>
        )}
        <button onClick={(e)=>{ e.stopPropagation(); onQuickNote(); }}
          className="text-[11px] inline-flex items-center gap-1 text-blue-700 hover:text-blue-800"
          data-testid={`lead-note-quick-${lead.id}`}>
          <StickyNote size={11}/> Nota
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Quick Note Dialog — atalho do card
// ─────────────────────────────────────────────────────────────────────────────
function QuickNoteDialog({ lead, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [pinned, setPinned] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); setPinned(false); }, [lead]);
  if (!lead) return null;

  const submit = async () => {
    if (!text.trim()) { toast.error("Escreva algo"); return; }
    setBusy(true);
    try {
      await api.post(`/master/crm/leads/${lead.id}/notes`, { text, pinned });
      toast.success("Observação adicionada");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally { setBusy(false); }
  };

  const notes = lead.notes_list || [];
  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-md max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="quick-note-dialog">
        <DialogHeader>
          <DialogTitle>Observações — {lead.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea rows={3} value={text} onChange={(e)=>setText(e.target.value)} placeholder="Anote o que conversaram, próximo passo, contexto…" data-testid="quick-note-textarea"/>
          <label className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={pinned} onChange={(e)=>setPinned(e.target.checked)} data-testid="quick-note-pinned-checkbox"/>
            Fixar no card (substitui a fixada atual)
          </label>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full" data-testid="quick-note-save-button">
            <Plus size={14} className="mr-1"/> {busy ? "Salvando…" : "Adicionar observação"}
          </Button>

          {notes.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-[11px] uppercase tracking-wider text-neutral-500">Últimas observações</p>
              {notes.slice().reverse().slice(0, 5).map(n => (
                <div key={n.id} className={`text-xs p-2 rounded border ${n.pinned ? "bg-amber-50 border-amber-200" : "bg-neutral-50 border-neutral-200"}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    {n.pinned && <Pin size={10} className="text-amber-600"/>}
                    <span className="text-[10px] text-neutral-500">{fmtDateTime(n.created_at)} · {n.by}</span>
                  </div>
                  <p className="whitespace-pre-wrap">{n.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// New Lead
// ─────────────────────────────────────────────────────────────────────────────
function NewLeadDialog({ open, stages, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", notes: "", stage_id: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", email: "", phone: "", company: "", notes: "", stage_id: stages[0]?.id || "" }); }, [open, stages]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }
    setBusy(true);
    try {
      await api.post("/master/crm/leads", form);
      toast.success("Lead criado");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar lead");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="new-lead-dialog">
        <DialogHeader><DialogTitle>Novo lead</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome *</Label><Input value={form.name} onChange={(e)=>setForm(p=>({...p, name: e.target.value}))} data-testid="lead-name-input"/></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>E-mail</Label><Input value={form.email} onChange={(e)=>setForm(p=>({...p, email: e.target.value}))}/></div>
            <div><Label>Telefone</Label><Input value={form.phone} onChange={(e)=>setForm(p=>({...p, phone: e.target.value}))}/></div>
          </div>
          <div><Label>Empresa</Label><Input value={form.company} onChange={(e)=>setForm(p=>({...p, company: e.target.value}))}/></div>
          <div>
            <Label>Etapa inicial</Label>
            <Select value={form.stage_id} onValueChange={(v)=>setForm(p=>({...p, stage_id: v}))}>
              <SelectTrigger><SelectValue placeholder="Escolha"/></SelectTrigger>
              <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Observações iniciais</Label><Textarea rows={3} value={form.notes} onChange={(e)=>setForm(p=>({...p, notes: e.target.value}))}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lead-save-button">{busy ? "Salvando…" : "Criar lead"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Lead Detail (tabs: Dados / Observações / Timeline)
// ─────────────────────────────────────────────────────────────────────────────
function LeadDetailDialog({ lead, stages, onClose, onChanged, onDeleted }) {
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("dados");
  const [noteText, setNoteText] = useState("");
  const [notePinned, setNotePinned] = useState(false);
  useEffect(() => {
    if (lead) {
      setForm({ ...lead, next_contact_at_local: isoToLocalInput(lead.next_contact_at) });
      setTab("dados");
      setNoteText(""); setNotePinned(false);
    } else { setForm(null); }
  }, [lead]);
  if (!lead || !form) return null;

  const save = async () => {
    try {
      await api.put(`/master/crm/leads/${lead.id}`, {
        name: form.name, email: form.email, phone: form.phone,
        company: form.company, stage_id: form.stage_id,
        next_contact_at: form.next_contact_at_local ? localInputToIso(form.next_contact_at_local) : null,
      });
      toast.success("Lead atualizado");
      onChanged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const addNote = async () => {
    if (!noteText.trim()) { toast.error("Escreva algo"); return; }
    try {
      await api.post(`/master/crm/leads/${lead.id}/notes`, { text: noteText, pinned: notePinned });
      toast.success("Observação adicionada");
      setNoteText(""); setNotePinned(false);
      // recarrega o lead
      const r = await api.get("/master/crm/leads", { _cache: false });
      const updated = (r.data || []).find(l => l.id === lead.id);
      if (updated) setForm({ ...updated, next_contact_at_local: isoToLocalInput(updated.next_contact_at) });
      onChanged();
    } catch (e) { toast.error("Erro ao adicionar"); }
  };

  const toggleNotePin = async (n) => {
    try {
      await api.put(`/master/crm/leads/${lead.id}/notes/${n.id}`, { text: n.text, pinned: !n.pinned });
      const r = await api.get("/master/crm/leads", { _cache: false });
      const updated = (r.data || []).find(l => l.id === lead.id);
      if (updated) setForm({ ...updated, next_contact_at_local: isoToLocalInput(updated.next_contact_at) });
      onChanged();
    } catch (e) { toast.error("Erro"); }
  };

  const deleteNote = async (n) => {
    if (!window.confirm("Excluir esta observação?")) return;
    try {
      await api.delete(`/master/crm/leads/${lead.id}/notes/${n.id}`);
      const r = await api.get("/master/crm/leads", { _cache: false });
      const updated = (r.data || []).find(l => l.id === lead.id);
      if (updated) setForm({ ...updated, next_contact_at_local: isoToLocalInput(updated.next_contact_at) });
      onChanged();
    } catch (e) { toast.error("Erro"); }
  };

  const del = async () => {
    if (!window.confirm(`Excluir lead "${lead.name}"?`)) return;
    try {
      await api.delete(`/master/crm/leads/${lead.id}`);
      toast.success("Lead excluído");
      onDeleted();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível excluir"); }
  };

  const notes = (form.notes_list || []).slice().reverse();
  const events = (form.events || []).slice().reverse();

  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="lead-detail-dialog">
        <DialogHeader><DialogTitle>{lead.name}</DialogTitle></DialogHeader>

        <div className="flex gap-1 border-b border-[#E5E5E5] mb-3">
          {[
            { k: "dados", label: "Dados" },
            { k: "notes", label: `Observações (${(form.notes_list || []).length})` },
            { k: "timeline", label: "Timeline" },
          ].map(t => (
            <button key={t.k} onClick={()=>setTab(t.k)}
              className={`px-3 py-2 text-sm border-b-2 transition ${tab===t.k ? "border-[#0A0A0A] text-[#0A0A0A] font-medium" : "border-transparent text-neutral-500 hover:text-neutral-700"}`}
              data-testid={`lead-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "dados" && (
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.name || ""} onChange={(e)=>setForm(p=>({...p, name: e.target.value}))}/></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>E-mail</Label><Input value={form.email || ""} onChange={(e)=>setForm(p=>({...p, email: e.target.value}))}/></div>
              <div><Label>Telefone</Label><Input value={form.phone || ""} onChange={(e)=>setForm(p=>({...p, phone: e.target.value}))}/></div>
            </div>
            <div><Label>Empresa</Label><Input value={form.company || ""} onChange={(e)=>setForm(p=>({...p, company: e.target.value}))}/></div>
            <div>
              <Label>Etapa</Label>
              <Select value={form.stage_id} onValueChange={(v)=>setForm(p=>({...p, stage_id: v}))}>
                <SelectTrigger data-testid="lead-stage-select"><SelectValue/></SelectTrigger>
                <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Próximo contato (data e hora)</Label>
              <Input type="datetime-local" value={form.next_contact_at_local || ""} onChange={(e)=>setForm(p=>({...p, next_contact_at_local: e.target.value}))} data-testid="lead-next-contact-input"/>
              <p className="text-[11px] text-neutral-500 mt-1">Aparece na agenda e no card do lead.</p>
            </div>
            {lead.source === "tenant" && (
              <div className="text-xs text-neutral-500 p-2 rounded bg-amber-50 border border-amber-200">
                Este lead foi gerado automaticamente a partir de uma loja. Para excluir, cancele/exclua a loja no painel Master.
              </div>
            )}
          </div>
        )}

        {tab === "notes" && (
          <div className="space-y-3">
            <div className="p-3 border border-[#E5E5E5] rounded-md bg-neutral-50">
              <Textarea rows={3} value={noteText} onChange={(e)=>setNoteText(e.target.value)} placeholder="Nova observação…" data-testid="lead-note-input"/>
              <div className="mt-2 flex items-center justify-between">
                <label className="text-xs flex items-center gap-1.5">
                  <input type="checkbox" checked={notePinned} onChange={(e)=>setNotePinned(e.target.checked)}/>
                  <Pin size={11}/> Fixar no card
                </label>
                <Button size="sm" onClick={addNote} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lead-add-note-button">
                  <Plus size={12} className="mr-1"/> Adicionar
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {notes.length === 0 && <p className="text-xs text-neutral-500 text-center py-4">Nenhuma observação ainda.</p>}
              {notes.map(n => (
                <div key={n.id} className={`p-2 rounded border ${n.pinned ? "bg-amber-50 border-amber-200" : "bg-white border-[#E5E5E5]"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] text-neutral-500 inline-flex items-center gap-1">
                      {n.pinned && <Pin size={10} className="text-amber-600"/>}
                      {fmtDateTime(n.created_at)} · {n.by}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={()=>toggleNotePin(n)} className="text-neutral-500 hover:text-amber-700" title={n.pinned ? "Desafixar" : "Fixar"}>
                        {n.pinned ? <PinOff size={12}/> : <Pin size={12}/>}
                      </button>
                      <button onClick={()=>deleteNote(n)} className="text-neutral-500 hover:text-red-600" title="Excluir">
                        <Trash2 size={12}/>
                      </button>
                    </div>
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{n.text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "timeline" && (
          <div className="space-y-2">
            {events.length === 0 && <p className="text-xs text-neutral-500 text-center py-4">Sem histórico ainda.</p>}
            {events.map(ev => (
              <div key={ev.id} className="flex gap-3 p-2 border-l-2 border-emerald-400 bg-neutral-50 rounded-r">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-neutral-500">{fmtDateTime(ev.at)} · {ev.by}</p>
                  {ev.type === "stage_change" && <p className="text-sm">Mudou etapa: <b>{ev.from || "—"}</b> → <b>{ev.to}</b></p>}
                  {ev.type === "schedule" && <p className="text-sm">Agendou contato para <b>{fmtDateTime(ev.scheduled_for)}</b></p>}
                  {ev.type === "note_added" && <p className="text-sm">Nova observação: <span className="text-neutral-700">{ev.preview}</span></p>}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          {lead.source !== "tenant" && (
            <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={del} data-testid="lead-delete-button"><Trash2 size={14} className="mr-1"/>Excluir</Button>
          )}
          <div className="flex-1"/>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lead-update-button">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Agenda
// ─────────────────────────────────────────────────────────────────────────────
function AgendaDialog({ open, onClose, onOpenLead, onChanged }) {
  const [agenda, setAgenda] = useState({ overdue: [], today: [], soon: [], upcoming: [] });
  const load = async () => {
    try {
      const r = await api.get("/master/crm/agenda", { _cache: false });
      setAgenda(r.data || {});
    } catch (e) {}
  };
  useEffect(() => { if (open) load(); }, [open]);

  const markDone = async (l) => {
    try {
      await api.put(`/master/crm/leads/${l.id}`, { next_contact_at: null });
      toast.success("Contato marcado como realizado");
      load();
      onChanged();
    } catch (e) { toast.error("Erro"); }
  };

  const buckets = [
    { key: "overdue", label: "Atrasados", color: "text-red-700 bg-red-50 border-red-200" },
    { key: "today",   label: "Hoje",      color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { key: "soon",    label: "Próximos 5 dias", color: "text-blue-700 bg-blue-50 border-blue-200" },
    { key: "upcoming",label: "Mais distantes",  color: "text-neutral-700 bg-neutral-50 border-neutral-200" },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="agenda-dialog">
        <DialogHeader>
          <DialogTitle><CalendarClock size={18} className="inline mr-1.5 -mt-1"/>Agenda</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {buckets.map(b => {
            const items = agenda[b.key] || [];
            return (
              <div key={b.key}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-block border ${b.color}`}>{b.label} · {items.length}</div>
                <div className="mt-2 space-y-2">
                  {items.length === 0 && <p className="text-xs text-neutral-400 pl-1">Nenhum contato.</p>}
                  {items.map(l => {
                    const wa = waUrl(l.phone, `Olá ${l.name || ""}, tudo bem?`);
                    return (
                      <div key={l.id} className="border border-[#E5E5E5] rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid={`agenda-item-${l.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{l.name}</p>
                          <p className="text-[11px] text-neutral-500">{fmtDateTime(l.next_contact_at)} {l.phone ? `· ${l.phone}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {wa && <Button size="sm" variant="outline" onClick={()=>window.open(wa, "_blank", "noopener,noreferrer")}><MessageCircle size={12} className="mr-1"/>WhatsApp</Button>}
                          <Button size="sm" variant="outline" onClick={()=>markDone(l)}>Realizado</Button>
                          <Button size="sm" onClick={()=>onOpenLead(l)} className="bg-[#0A0A0A] hover:bg-black text-white">Abrir</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Manage Stages (criar/renomear/cor/excluir)
// ─────────────────────────────────────────────────────────────────────────────
function ManageStagesDialog({ open, onClose, stages, onChanged }) {
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#10B981");
  const [editing, setEditing] = useState(null); // {id, name, color}
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setNewName(""); setEditing(null); } }, [open]);

  const create = async () => {
    if (!newName.trim()) { toast.error("Nome obrigatório"); return; }
    setBusy(true);
    try {
      await api.post("/master/crm/stages", { name: newName, color: newColor });
      toast.success("Etapa criada");
      setNewName(""); setNewColor("#10B981");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing || !editing.name.trim()) return;
    setBusy(true);
    try {
      await api.put(`/master/crm/stages/${editing.id}`, { name: editing.name, color: editing.color });
      toast.success("Etapa renomeada");
      setEditing(null);
      onChanged();
    } catch (e) { toast.error("Erro"); }
    finally { setBusy(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Excluir a etapa "${s.name}"?`)) return;
    try {
      await api.delete(`/master/crm/stages/${s.id}`);
      toast.success("Etapa excluída");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível excluir"); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-auto sm:max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="manage-stages-dialog">
        <DialogHeader>
          <DialogTitle><Settings size={18} className="inline mr-1.5 -mt-1"/>Gerenciar etapas</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-2">
            {stages.map(s => (
              <div key={s.id} className="flex items-center gap-2 p-2 border border-[#E5E5E5] rounded-md" data-testid={`stage-row-${s.id}`}>
                {editing?.id === s.id ? (
                  <>
                    <input type="color" value={editing.color} onChange={(e)=>setEditing(p=>({...p, color: e.target.value}))} className="w-8 h-8 rounded border-0 cursor-pointer"/>
                    <Input value={editing.name} onChange={(e)=>setEditing(p=>({...p, name: e.target.value}))} className="flex-1" data-testid={`stage-name-input-${s.id}`}/>
                    <Button size="sm" onClick={saveEdit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`stage-save-${s.id}`}>Salvar</Button>
                    <Button size="sm" variant="outline" onClick={()=>setEditing(null)}><X size={12}/></Button>
                  </>
                ) : (
                  <>
                    <span className="h-6 w-6 rounded-full flex-shrink-0" style={{ background: s.color }}/>
                    <span className="text-sm flex-1 truncate">{s.name}</span>
                    <Button size="sm" variant="outline" onClick={()=>setEditing({ id: s.id, name: s.name, color: s.color })} data-testid={`stage-edit-${s.id}`}><Edit2 size={12}/></Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={()=>del(s)} data-testid={`stage-delete-${s.id}`}><Trash2 size={12}/></Button>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-[#E5E5E5]">
            <Label>Nova etapa</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={newColor} onChange={(e)=>setNewColor(e.target.value)} className="w-10 h-10 rounded border-0 cursor-pointer"/>
              <Input value={newName} onChange={(e)=>setNewName(e.target.value)} placeholder="Nome da etapa" data-testid="new-stage-name-input"/>
              <Button onClick={create} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-stage-create-button">
                <Plus size={14} className="mr-1"/>Criar
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
